import { useEffect } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import * as p from 'src/constants/page-classnames';
import * as routes from './constants/routes';
import Article from './pages/article/article';
import CookiesPolicy from './pages/policies/cookies-policy';
import BusinessMap from './pages/business-map/business-map';
import Courses from './pages/courses/courses';
import Events from './pages/events/events';
import EventDetails from './pages/events/details/event-details';
import General from './pages/general/general';
import GlobalSearch from './pages/global-search/global-search';
import JobSearch from './pages/job-search/job-search';
import VacancyDetail from './pages/job-search/vacancy-detail';
import NotFound from './pages/not-found/not-found';
import Partnership from './pages/partnership/partnership';
import ServicesPage from './pages/services/services-page';
import startGoogleAnalytics from './utils/analytics/google';
import News from './pages/news/news';
import SingleNews from './pages/news/single-news';
import KnowledgeBase from './pages/knowledge-base/knowledge-base';
import Section from './pages/knowledge-base/details/section';
import Module from './pages/knowledge-base/details/module';
import PersonalDataPolicy from './pages/policies/personal-data';
import Contacts from './pages/contacts/contacts';
import ContactBlockDetails from './pages/contacts/details/block-details';
import AboutPage from './pages/about-page/about-page';
import { useMetaContext } from './hooks/meta-context';
import CustomPage from './pages/custom-page/custom-page';

function App() {
  const { useUpdateRoutesMeta } = useMetaContext();

  useUpdateRoutesMeta();
  useEffect(() => {
    startGoogleAnalytics();
  }, []);

  return (
    <Routes>
      <Route path={routes.GENERAL.relativePath} element={<General />} />
      <Route path={routes.GLOBAL_SEARCH.relativePath} element={<GlobalSearch />} />
      <Route path={routes.PARTNERSHIP.relativePath} element={<Partnership />} />
      <Route path={routes.ABOUT.relativePath} element={<AboutPage />} />

      <Route path='articles/:slug' element={<Article />} />
      <Route path='pages/:slug/events/:slug' element={<EventDetails />} />
      <Route path='pages/:slug' element={<CustomPage />} />

      <Route path={routes.EVENTS.relativePath}>
        <Route index element={<Events />} />
        <Route path=':slug' element={<EventDetails />} />
        <Route path='pages/:slug' element={<CustomPage />} />
      </Route>

      <Route path={routes.WORK.relativePath} element={<Outlet />}>
        <Route index element={<Navigate to={routes.WORK_SEARCH.relativePath} replace />} />
        <Route path={routes.WORK_SEARCH.relativePath} element={<JobSearch />} />
        <Route path={routes.VACANCY_PAGE.relativePath + '/:slug'} element={<VacancyDetail />} />
        <Route path={routes.CAREER_INFO.relativePath} element={<Outlet />}>
          <Route
            index
            element={<ServicesPage key={p.CAREER_INFO} pageName={routes.CAREER_INFO.title} className={p.CAREER_INFO} />}
          />
          <Route path='articles/:slug' element={<Article route={routes.CAREER_INFO} />} />
          <Route path='pages/:slug' element={<CustomPage />} />
        </Route>
      </Route>

      <Route path={routes.COOKIES_POLICIES.relativePath} element={<Outlet />}>
        <Route path='' element={<CookiesPolicy />} />
      </Route>
      <Route path={routes.PERSONAL_DATA_POLICIES.relativePath} element={<Outlet />}>
        <Route path='' element={<PersonalDataPolicy />} />
      </Route>

      <Route path={routes.EDUCATION.relativePath} element={<Outlet />}>
        <Route index element={<Navigate to={routes.EDUCATION_COURSES.relativePath} replace />} />
        <Route path={routes.EDUCATION_COURSES.relativePath} element={<Courses />} />
        <Route path={routes.EDUCATION_INFO.relativePath} element={<Outlet />}>
          <Route
            index
            element={<ServicesPage key={p.EDUCATION} pageName={routes.EDUCATION_INFO.title} className={p.EDUCATION} />}
          />
          <Route path='articles/:slug' element={<Article route={routes.EDUCATION_INFO} />} />
          <Route path='pages/:slug' element={<CustomPage />} />
        </Route>
      </Route>

      <Route path={routes.BUSINESS_MAP.relativePath} element={<BusinessMap />} />

      <Route path={routes.CONTACTS.relativePath} element={<Outlet />}>
        <Route path='' element={<Contacts />} />
        <Route path=':slug' element={<ContactBlockDetails />} />
      </Route>

      <Route path={routes.VETERAN_LOCATIONS.relativePath} element={<Outlet />}>
        <Route
          index
          element={
            <ServicesPage
              key={p.VETERAN_LOCATIONS}
              pageName={routes.VETERAN_LOCATIONS.title}
              className={p.VETERAN_LOCATIONS}
            />
          }
        />
        <Route path='articles/:slug' element={<Article route={routes.VETERAN_LOCATIONS} />} />
        <Route path='pages/:slug' element={<CustomPage />} />
      </Route>

      <Route path={routes.PSYCHOLOGICAL_HELP.relativePath} element={<Outlet />}>
        <Route
          index
          element={
            <ServicesPage
              key={p.PSYCHOLOGICAL_HELP}
              pageName={routes.PSYCHOLOGICAL_HELP.title}
              className={p.PSYCHOLOGICAL_HELP}
            />
          }
        />
        <Route path='articles/:slug' element={<Article route={routes.PSYCHOLOGICAL_HELP} />} />
        <Route path='pages/:slug' element={<CustomPage />} />
      </Route>

      <Route path={routes.MEDICAL_SERVICES.relativePath} element={<Outlet />}>
        <Route
          index
          element={
            <ServicesPage
              key={p.MEDICAL_SERVICES}
              pageName={routes.MEDICAL_SERVICES.title}
              className={p.MEDICAL_SERVICES}
            />
          }
        />
        <Route path='articles/:slug' element={<Article route={routes.MEDICAL_SERVICES} />} />
        <Route path='pages/:slug' element={<CustomPage />} />
      </Route>

      <Route path={routes.LEGAL_HELP.relativePath} element={<Outlet />}>
        <Route
          index
          element={<ServicesPage key={p.LEGAL_HELP} pageName={routes.LEGAL_HELP.title} className={p.LEGAL_HELP} />}
        />
        <Route path='articles/:slug' element={<Article route={routes.LEGAL_HELP} />} />
        <Route path='pages/:slug' element={<CustomPage />} />
      </Route>

      <Route path={routes.USEFUL_INFO.relativePath} element={<Outlet />}>
        <Route
          index
          element={<ServicesPage key={p.USEFUL_INFO} pageName={routes.USEFUL_INFO.title} className={p.USEFUL_INFO} />}
        />
        <Route path='articles/:slug' element={<Article route={routes.USEFUL_INFO} />} />
        <Route path='pages/:slug' element={<CustomPage />} />
      </Route>

      <Route path='services/:slug' element={<Outlet />}>
        <Route index element={<ServicesPage key={p.PAGE} pageName={''} className={p.PAGE} />} />
        <Route path='articles/:slug' element={<Article route={routes.GENERAL} />} />
        <Route path='pages/:slug' element={<CustomPage />} />
      </Route>

      <Route path={routes.NEWS.relativePath}>
        <Route index element={<News />} />
        <Route path=':slug' element={<SingleNews />} />
      </Route>
      <Route path={routes.KNOWLEDGE_BASE.relativePath}>
        <Route index element={<KnowledgeBase />} />
        <Route path=':slug' element={<Section />} />
        <Route path=':slug/:module' element={<Module />} />
        <Route path='articles/:slug' element={<Article route={routes.KNOWLEDGE_BASE} />} />
      </Route>
      <Route path='404' element={<NotFound />} />
      <Route path='*' element={<NotFound />} />
    </Routes>
  );
}

export default App;
